import PageSection from 'components/PageSection';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { carrySettings } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Spacing from 'components/Spacing';
import { Row } from 'components/Layout';
import { AddOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { CarriedInterestFeeTemplate } from 'further-types/lp/carry-settings';
import useCarriedInterestFeeTemplatesOptions from './hooks/useCarriedInterestFeeTemplatesOptions';
import CarriedInterestFeeTemplateComponent from './CarriedInterestFeeTemplate';
import { z } from 'zod';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    carrySettings: {
      carriedInterestFeeTemplates: Array<
        CarriedInterestFeeTemplate & { isInUse: boolean }
      >;
    };
  };
};

const CarriedInterestFeeTemplates: React.FC<Props> = ({ vehicle }) => {
  const formMethods = useForm<FormValues>({
    resolver: zodResolver(carrySettings.CarriedInterestFeeTemplatesSettings),
    shouldFocusError: true,
    values: vehicle.carrySettings,
    criteriaMode: 'all',
  });

  const templateIdsInUse = vehicle.carrySettings.carriedInterestFeeTemplates
    .filter((template) => template.isInUse)
    .map((template) => template._id);

  const { control, handleSubmit } = formMethods;

  const { defaultTemplate } = useCarriedInterestFeeTemplatesOptions();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'carriedInterestFeeTemplates',
  });

  const handleRemoveTemplate = (index: number) => {
    if (fields.length < 2) return;

    remove(index);
  };

  const options = useHandleTrpcMutation()({
    successMessage: 'Carried interest fee templates settings updated.',
  });

  const { mutate: updateSettings, isPending } =
    api.vehicle.updateCarriedInterestFeeTemplatesSettings.useMutation(options);

  const onSubmit = (data: FormValues) => {
    updateSettings({
      vehicleId: vehicle._id,
      ...data,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Spacing>
          {fields.map((field, index) => (
            <PageSection
              showDivider
              withSpacing
              key={field.id}
              headerContent={
                <Row centered fullWidth justify="space-between" spacing="sm">
                  <Typography variant="h4">
                    Carried interest fee template {index + 1}
                  </Typography>
                  <ButtonGroup>
                    <Button
                      size="large"
                      variant="text"
                      color="error"
                      onClick={() => handleRemoveTemplate(index)}
                      disabled={
                        fields.length < 2 ||
                        (!!field._id && templateIdsInUse.includes(field._id))
                      }
                    >
                      Remove fee template
                    </Button>
                  </ButtonGroup>
                </Row>
              }
            >
              <CarriedInterestFeeTemplateComponent
                index={index}
                control={control}
              />
            </PageSection>
          ))}
          {fields.length > 0 && (
            <ButtonGroup>
              <Button
                size="large"
                loading={isPending}
                variant="contained"
                type="submit"
              >
                Save changes
              </Button>
            </ButtonGroup>
          )}

          <ButtonGroup>
            <Button
              size="large"
              variant="outlined"
              type="button"
              onClick={() => append(defaultTemplate)}
              startIcon={<AddOutlined />}
            >
              Add new fee template
            </Button>
          </ButtonGroup>
        </Spacing>
      </form>
    </FormProvider>
  );
};

export default CarriedInterestFeeTemplates;
